// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import getProductsActiveState from "@awc/App/Widgets/SearchEngineOptimization/ActiveState/Products";
import { SeoFieldsProps } from "@awc/App/Widgets/SearchEngineOptimization/Fields";
import { SeoAppProps, SeoHandlerProps } from "@awc/App/Widgets/SearchEngineOptimization/Type";
import { Offer, Product, ProductCollection, WithContext } from "@awc/App/Widgets/SearchEngineOptimization/Type/schema";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { AvailabilityMessageType, ProductModel } from "@insite/client-framework/Types/ApiModels";

/**
 * get availability url based on real time inventory
 * @param productInfo
 */
export const getAvailability = (productInfo: ProductInfo | undefined) => {
    if (!productInfo) {
        return "https://schema.org/OutOfStock";
    }

    const availability = productInfo.inventory?.inventoryAvailabilityDtos?.find(
        o => o.unitOfMeasure.toLowerCase() === (productInfo?.unitOfMeasure?.toLowerCase() || ""),
    )?.availability;

    switch (availability?.messageType) {
        case AvailabilityMessageType.Available:
            return "https://schema.org/InStock";
        case AvailabilityMessageType.LowStock:
            return "https://schema.org/InStock";
        case AvailabilityMessageType.OutOfStock:
            return "https://schema.org/OutOfStock";
        default:
            return "https://schema.org/OutOfStock";
    }
};

/**
 * Create Schema for Product
 * @param product
 * @param productInfo
 * @param options
 */
const createProductSchema = (
    product: ProductModel,
    productInfo?: ProductInfo,
    options?: {
        url?: string;
        currency?: string;
        fields?: SeoFieldsProps;
    },
): Product => {
    let productPrice = 0;
    if (productInfo?.pricing) {
        const priceDisplay = getUnitNetPrice(productInfo?.pricing, productInfo?.qtyOrdered || 1).priceDisplay;
        productPrice = Number((priceDisplay || "0").replace(/[^0-9\.-]+/g, ""));
    }
    const unitOfMeasure = productInfo?.pricing?.unitOfMeasure;
    const parts = unitOfMeasure?.split(" ") ?? [];
    const value = parts.length > 1 ? parts[0] : "1";
    const unitCode = parts.length > 1 ? parts[1] : parts[0];

    // If Review and Rating data is available, that data should be piped into the following variables and added into the item object below.
    // More information for this schema can be found on https://developers.google.com/search/docs/appearance/structured-data/merchant-listing
    // Special note, some 3rd party review and rating partners will include structured data in their feed. If this is the case, it shouldn't be duplicated.
    // const productReview = {
    //     "@type": "Review",
    //     "reviewRating": {
    //       "@type": "Rating",
    //       "ratingValue": 4,
    //       "bestRating": 5
    //     },
    //     "author": {
    //       "@type": "Person",
    //       "name": "Person's Name"
    //     }
    // };
    // const productRating = {
    //     "@type": "AggregateRating",
    //     "ratingValue": 4.4,
    //     "reviewCount": 89
    // }

    const item: Product = {
        "@type": "Product",
        name: product.productTitle,
        description: product.content?.htmlContent.replace(/(<([^>]+)>)/gi, "") || product.productTitle,
        image: product.mediumImagePath,
        productID: product.productNumber,
        url: `${options?.url}${product.canonicalUrl || product.urlSegment}`,
        sku: product.detail?.sku || product.productNumber,
        mpn: product.manufacturerItem,
        gtin: product.properties?.gtin || product.properties?.ean || product.detail?.upcCode,
        brand: {
            "@type": "Brand",
            name: product.brand?.name,
        },
        offers: {
            "@type": "Offer",
            priceCurrency: options?.currency || "USD",
            url: `${options?.url}${product.canonicalUrl || product.urlSegment}`,
            price: productPrice,
            itemCondition: "https://schema.org/NewCondition",
            seller: {
                "@type": "Organization",
                name: options?.fields?.organizationName,
            },
            priceSpecification: {
                "@type": "UnitPriceSpecification",
                price: productPrice,
                priceCurrency: options?.currency || "USD",
                referenceQuantity: {
                    "@type": "QuantitativeValue",
                    value,
                    unitCode,
                },
            },
        },
        // review: productReview,
        // aggregateRating: productRating,
    };

    if (getAvailability(productInfo) !== "https://schema.org/OutOfStock") {
        item.offers = {
            ...(item.offers as Offer),
            availability: getAvailability(productInfo),
        };
    }

    if (product.brand) {
        item.brand = {
            "@type": "Brand",
            name: product.brand?.name,
            url: `${options?.url}${product.brand?.detailPagePath}`,
            logo: product.brand?.logoLargeImagePath,
        };
    }

    if (product.attributeTypes && product.attributeTypes?.length > 0) {
        item.additionalProperty = [];

        product.attributeTypes.forEach(attributeType => {
            attributeType.attributeValues?.forEach(attributeValue => {
                const attributeName = attributeType.label || attributeType.name;
                (item.additionalProperty as Product["additionalProperty"][])!.push({
                    "@type": "PropertyValue",
                    name: attributeName.includes(":") ? attributeName.split(":")[1] : attributeName,
                    value: attributeValue.valueDisplay,
                });
            });
        });
    }

    return item;
};

/**
 * Build the structured data
 */
const loadSchemaProduct = ({
    state,
    fields,
    payload,
    url,
    currentPage,
}: SeoAppProps): WithContext<Product | ProductCollection> => {
    const isProductDetailsPage = currentPage.type === "ProductDetailsPage";
    const currency = getSession(state).currency?.currencyCode;

    const products = payload.products;
    const productInfoList = isProductDetailsPage
        ? state.pages.productDetails.productInfosById
        : state.pages.productList.productInfosByProductId;

    if (!isProductDetailsPage) {
        const schemaData: WithContext<ProductCollection> = {
            "@context": "https://schema.org",
            "@type": "ProductCollection",
        };

        schemaData.mainEntity =
            products?.map(product =>
                createProductSchema(product, productInfoList?.[product.id], {
                    url,
                    currency,
                }),
            ) || [];

        return schemaData;
    }

    /**
     * For ProductDetails Page
     */

    let schemaData: WithContext<Product> = {
        "@context": "https://schema.org",
        "@type": "Product",
    };

    if (!products?.[0]) {
        return schemaData;
    }
    const productInfo = productInfoList?.[products[0].id];
    const productItemSchema = createProductSchema(products[0], productInfo, {
        url,
        currency,
        fields,
    });

    // XNG-Changes  - AWC Customization
    const linkedData = products[0].properties["linkedData"];

    schemaData = {
        ...schemaData,
        ...productItemSchema,
        // XNG-Changes  - AWC Customization
        linkedData,
    } as any;

    return schemaData;
};

export default {
    isActivate: getProductsActiveState,
    buildSchema: loadSchemaProduct,
} as SeoHandlerProps;
